<template>
  <div class="chunked-video-uploader">
    <h2>Carga del Video</h2>
    <input type="file" @change="handleFileChange" accept="video/*" />
    <button @click="uploadFile" :disabled="!selectedFile || uploading">
      {{ uploading ? "Cargandose..." : "Carga de Video" }}
    </button>
    <div v-if="uploading">
      <p>Progreso: {{ uploadProgress.toFixed(2) }}%</p>
      <div class="progress-bar-container">
        <div
          class="progress-bar"
          :style="{ width: uploadProgress + '%' }"
        ></div>
      </div>
    </div>
    <div v-if="message" :class="messageType">{{ message }}</div>
    <div v-if="publicUrl">
      <p>Video ha sido cargado satifactoriamente! URL :</p>
      <a :href="publicUrl" target="_blank">{{ publicUrl }}</a>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "ChunkedVideoUploader",
  data() {
    return {
      selectedFile: null,
      uploading: false,
      uploadProgress: 0,
      message: "",
      messageType: "",
      publicUrl: "",
      chunkSize: 1024 * 1024 * 5, // 5MB chunks
    };
  },
  methods: {
    handleFileChange(event) {
      this.selectedFile = event.target.files[0];
      this.message = "";
      this.messageType = "";
      this.uploadProgress = 0;
      this.publicUrl = "";
    },
    async uploadFile() {
      if (!this.selectedFile) {
        this.message = "Please select a file first.";
        this.messageType = "error";
        return;
      }

      this.uploading = true;
      this.message = "Starting upload...";
      this.messageType = "info";
      this.uploadProgress = 0;
      this.publicUrl = "";

      // Configure axios to send cookies with requests
      axios.defaults.withCredentials = true;
      const API_URL = process.env.VUE_APP_API_URL;

      // Fetch CSRF cookie
      try {
        await axios.get(`${API_URL}/sanctum/csrf-cookie`);
      } catch (error) {
        console.error("CSRF cookie fetch error:", error);
        this.message = `Failed to fetch CSRF token: ${error.message}`;
        this.messageType = "error";
        this.uploading = false;
        return;
      }

      const totalChunks = Math.ceil(this.selectedFile.size / this.chunkSize);
      let uploadedChunks = 0;

      for (let i = 0; i < totalChunks; i++) {
        const start = i * this.chunkSize;
        const end = Math.min(start + this.chunkSize, this.selectedFile.size);
        const chunk = this.selectedFile.slice(start, end);

        const formData = new FormData();
        formData.append("file", chunk);
        formData.append("fileName", this.selectedFile.name);
        formData.append("chunkIndex", i);
        formData.append("totalChunks", totalChunks);
        formData.append("fileSize", this.selectedFile.size);
        formData.append("fileType", this.selectedFile.type);

        try {
          const response = await axios.post(
            `${API_URL}/api/video-upload/chunk`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
                // Add authorization header if your API requires it (e.g., Bearer token)
                // 'Authorization': `Bearer YOUR_AUTH_TOKEN`
              },
              onUploadProgress: (progressEvent) => {
                const chunkProgress =
                  (progressEvent.loaded / progressEvent.total) *
                  (100 / totalChunks);
                this.uploadProgress =
                  (uploadedChunks * 100) / totalChunks + chunkProgress;
              },
            }
          );

          if (response.data.message === "Chunk received") {
            uploadedChunks++;
            this.message = `Uploading chunk ${i + 1} of ${totalChunks}...`;
          } else if (response.data.message === "File uploaded successfully") {
            this.message = "File upload complete!";
            this.messageType = "success";
            this.publicUrl = response.data.publicUrl;
            this.uploadProgress = 100;
          }
        } catch (error) {
          console.error("Upload error:", error);
          let errorMessage = error.response?.data?.message || error.message;
          if (error.response?.data?.errors) {
            errorMessage +=
              "\n" +
              Object.values(error.response.data.errors).flat().join("\n");
          }
          this.message = `Upload failed: ${errorMessage}`;
          this.messageType = "error";
          this.uploading = false;
          this.uploadProgress = (uploadedChunks * 100) / totalChunks; // Show progress up to the failed chunk
          return;
        }
      }
      this.uploading = false;
    },
  },
};
</script>

<style scoped>
.chunked-video-uploader {
  font-family: Arial, sans-serif;
  padding: 20px;
  border: 1px solid #ccc;
  border-radius: 8px;
  max-width: 600px;
  margin: 20px auto;
  background-color: #f9f9f9;
}

h2 {
  color: #333;
  margin-bottom: 20px;
}

input[type="file"] {
  display: block;
  margin-bottom: 15px;
}

button {
  background-color: #007bff;
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 16px;
}

button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.progress-bar-container {
  width: 100%;
  background-color: #e0e0e0;
  border-radius: 5px;
  margin-top: 10px;
  height: 20px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background-color: #28a745;
  width: 0%;
  border-radius: 5px;
  text-align: center;
  color: white;
  line-height: 20px;
  transition: width 0.3s ease-in-out;
}

.message {
  margin-top: 15px;
  padding: 10px;
  border-radius: 5px;
}

.info {
  background-color: #e7f3fe;
  color: #0056b3;
  border: 1px solid #b8daff;
}

.success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.error {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

a {
  color: #007bff;
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}
</style>
